import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-light-svg-icons/faArrowUpRightFromSquare';
import { TypedMemo } from '../../common/types';
import { platformMeta } from '../promote/platformMeta';
import { postPermalink } from '../promote/socialNumbers';
import { SerializedSocialPost } from '../promote/types';
import { Music24Mark } from './Music24Mark';
import { SocialPostThumb } from './SocialPostThumb';
import { SocialPostTypeBadge } from './SocialPostTypeBadge';
import { socialMessages as messages } from './socialMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet per screen; each part of the screen uses its own slice of it */
import styles from './Social.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SocialPostDetailHeadProps {
  post: SerializedSocialPost;
  /** Opens the Promote calendar. Only a post that Music24 published has the link. */
  onOpenCalendar: () => void;
}

/** The head of the post detail: the picture, the channel, the type, the date, the caption, the links. */
const SocialPostDetailHeadComponent: React.FC<SocialPostDetailHeadProps> = ({ post, onOpenCalendar }) => {
  const intl = useIntl();
  const meta = platformMeta[post.platform];
  const publishedAt = new Date(post.publishedAt);
  const isOurs = post.scheduledPostUuid !== null;
  // The address comes from a platform: only an http or https address becomes a link.
  const permalink = postPermalink(post);

  return (
    <div className={styles.detailHead}>
      <SocialPostThumb post={post} size="lg" />
      <div className={styles.detailHeadText}>
        <div className={styles.detailMeta}>
          <span style={{ color: meta.color }}>
            <FontAwesomeIcon icon={meta.icon} />
          </span>
          <b>{meta.name}</b>
          <SocialPostTypeBadge type={post.type} />
          <span>
            {intl.formatDate(publishedAt, { dateStyle: 'medium' })} ·{' '}
            {intl.formatTime(publishedAt, { hour: '2-digit', minute: '2-digit' })}
          </span>
          {isOurs ? <Music24Mark label={intl.formatMessage(messages.filterOurs)} /> : null}
        </div>
        {post.caption === null || post.caption === '' ? null : <p className={styles.detailCaption}>{post.caption}</p>}
        <div className={styles.detailLinks}>
          {permalink === null ? null : (
            <a className={styles.detailLink} href={permalink} target="_blank" rel="noopener noreferrer">
              {intl.formatMessage(messages.detailOpenOn, { platform: meta.name })}
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
            </a>
          )}
          {isOurs ? (
            <button type="button" className={clsx(styles.detailLink, styles.detailLinkQuiet)} onClick={onOpenCalendar}>
              {intl.formatMessage(messages.detailOpenCalendar)}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export const SocialPostDetailHead = TypedMemo(SocialPostDetailHeadComponent);
